import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { authService } from "@/api/services";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadProfile = useCallback(async () => {
        const token = localStorage.getItem("kl_token");
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const me = await authService.me();
            setUser(me);
        } catch (err) {
            // Token invalid/expired — clean up but don't crash app
            localStorage.removeItem("kl_token");
            setUser(null);
            // eslint-disable-next-line no-console
            console.warn("Auth restore failed:", err?.message || err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const login = useCallback(async (email, password) => {
        const data = await authService.login({ email, password });
        localStorage.setItem("kl_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (name, email, password, extras = {}) => {
        const data = await authService.register({ name, email, password, ...extras });
        return data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("kl_token");
        setUser(null);
    }, []);

    const refresh = useCallback(async () => {
        const me = await authService.me();
        setUser(me);
        return me;
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh, setUser }),
        [user, loading, login, register, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside <AuthProvider>");
    }
    return ctx;
}
